import React, { useEffect, useMemo, useState } from 'react';
import { businesses as bizApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Ticket, Check, Loader2 } from 'lucide-react';
import CategoryChips from '../components/CategoryChips';
import ClampWithMore from '../components/ClampWithMore';

export default function Coupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('All');
  const { user, setUserData } = useAuth();

  useEffect(() => {
    bizApi.list().then(setItems).finally(() => setLoading(false));
  }, []);

  const redeemed = new Set(user?.redeemedCoupons || []);

  const counts = useMemo(() => {
    const m = { All: items.length };
    items.forEach(b => { if (b.category) m[b.category] = (m[b.category] || 0) + 1; });
    return m;
  }, [items]);

  const filtered = useMemo(() =>
    cat === 'All' ? items : items.filter(b => b.category === cat),
  [items, cat]);

  const redeem = async (b) => {
    if (redeemed.has(b.id)) return;
    try {
      const res = await bizApi.redeem(b.id);
      setUserData(res.user);
      toast({ title: 'Coupon redeemed!', description: `+${res.points_added} pts • ${b.coupon} at ${b.name}` });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Coupons & Deals</h1>
        <p className="text-gray-400 text-sm">Redeem coupons at any PBB business. Save + earn points.</p>
      </div>
      <CategoryChips value={cat} onChange={setCat} counts={counts} testId="coupons-cats" />
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="coupons-empty-state">
          <Ticket className="mx-auto text-green-400" size={30} />
          <h3 className="mt-4 text-xl font-bold">Coupons roll out with our founding businesses</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">Be one of the first 100 members — register now to unlock every founding business coupon the moment they publish.</p>
          <a href="/signup" data-testid="coupons-empty-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">Register Free</a>
        </div>
      ) : filtered.length === 0 ? (
        <div className="pbb-card p-8 text-sm text-gray-400 text-center">No coupons in <b>{cat}</b> yet — check back soon.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(b => {
            const done = redeemed.has(b.id);
            return (
              <div key={b.id} className="pbb-card overflow-hidden">
                <div className="h-1.5 bg-green-500" />
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={b.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    <div>
                      <div className="font-semibold text-sm pbb-card-title" title={b.name}>{b.name}</div>
                      <div className="text-xs text-gray-400">{b.city}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-green-400">
                    <Ticket size={16}/> <span className="font-semibold">{b.coupon}</span>
                  </div>
                  <ClampWithMore text={b.description} lines={3} className="text-xs text-gray-400 mt-2" testId={`coupon-desc-${b.id}`} />
                  <div className="text-xs text-gray-400 mt-1">Earn +{b.points} pts on redemption</div>
                  <button onClick={() => redeem(b)} disabled={done}
                    className={`mt-3 w-full px-3 py-2 rounded-lg text-sm ${done ? 'bg-[#0d1f14] border border-[#153b23] text-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}>
                    {done ? <span className="inline-flex items-center gap-1 justify-center"><Check size={14}/> Redeemed</span> : 'Redeem coupon'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
